/**
 * Phase 2 submission payload builders.
 *
 * Frontend forms keep rich v1 contract state (arrays, city/state, etc.).
 * These builders produce the transport payload for current API endpoints,
 * preserving backward-compatible field names where required.
 *
 * See docs/submission-contracts-v1.md for the full contract and encodings.
 */

import {
  CONTRACT_VERSION,
  partnerInterestTypeForSelections,
} from "@/constants/formOptions";
import { loadStoredAttribution, sanitizeAttribution } from "@/lib/attribution";

function trimOrNull(v) {
  const s = (v ?? "").toString().trim();
  return s || null;
}

function joinLocation(city, state) {
  const c = (city || "").trim();
  const s = (state || "").trim();
  if (c && s) return `${c}, ${s}`;
  return c || s || "";
}

/** Attach Phase 3D spam-control fields when present on form state. */
export function withSpamFields(payload, form = {}) {
  return {
    ...payload,
    hp_company: form.hp_company || "",
    form_started_at: form.form_started_at || undefined,
  };
}

/** Attach Phase 4A attribution without overwriting heard_about. */
export function withAttributionFields(payload, form = {}) {
  const fromForm = sanitizeAttribution(form);
  const fromSession = loadStoredAttribution() || {};
  const attr = { ...fromSession, ...fromForm };
  return Object.keys(attr).length ? { ...payload, ...attr } : payload;
}

function finalizePayload(payload, form = {}) {
  return withAttributionFields(withSpamFields(payload, form), form);
}

/** Encode a string array for multipart FormData (JSON string). */
export function encodeArrayForTransport(arr) {
  if (!arr || arr.length === 0) return null;
  return JSON.stringify(arr);
}

export function buildTalentApiPayload(form) {
  const talentTypes = form.talent_types || [];
  const roleTypes = form.role_types || [];
  const specialSkills = form.special_skills || [];
  const languages = form.languages || [];
  const additionalCrewRoles = form.additional_crew_roles || [];
  const availableForCrew = form.available_for_crew || "";
  const isTalentPlusCrew = availableForCrew === "Yes";

  // Legacy singular fields required by current casting endpoints.
  const talentTypePrimary = talentTypes[0] || "";
  const roleInterest =
    roleTypes.length > 0 ? roleTypes.join(", ") : trimOrNull(form.role_interest);

  return finalizePayload(
    {
      contract_version: CONTRACT_VERSION,
      submission_type: "talent",
      // Legacy API fields
      name: form.full_name.trim(),
      email: form.email.trim(),
      phone: form.phone.trim(),
      location: joinLocation(form.city, form.state),
      talent_type: talentTypePrimary,
      union_status: form.union_status,
      role_interest: roleInterest,
      headshot_link: trimOrNull(form.headshot_link),
      resume_link: trimOrNull(form.resume_link),
      reel_portfolio_link: trimOrNull(form.reel_link),
      imdb_link: trimOrNull(form.imdb_link),
      notes: trimOrNull(form.notes),
      heard_about: form.referral_source || null,
      consent: !!form.consent,
      // v1 expansions (JSON path; multipart encodes arrays as JSON strings)
      stage_name: trimOrNull(form.stage_name),
      city: form.city.trim(),
      state: form.state.trim(),
      willing_to_travel: form.willing_to_travel || null,
      representation: trimOrNull(form.representation),
      talent_types: talentTypes,
      talent_type_other: trimOrNull(form.talent_type_other),
      role_types: roleTypes,
      playing_age_range: trimOrNull(form.playing_age_range),
      height: trimOrNull(form.height),
      union_status_other: trimOrNull(form.union_status_other),
      languages,
      special_skills: specialSkills,
      special_skills_other: trimOrNull(form.special_skills_other),
      website: trimOrNull(form.website),
      instagram: trimOrNull(form.instagram),
      referral_source: form.referral_source || null,
      referral_source_other: trimOrNull(form.referral_source_other),
      available_for_crew: availableForCrew || null,
      additional_crew_roles: isTalentPlusCrew ? additionalCrewRoles : [],
      primary_category: "Talent",
      additional_categories: isTalentPlusCrew ? ["Production Network"] : [],
      category_label: isTalentPlusCrew ? "Talent + Crew" : "Talent",
    },
    form,
  );
}

/**
 * Flatten talent payload for multipart: arrays become JSON strings.
 * Files are attached separately by submitCasting.
 */
export function flattenTalentPayloadForMultipart(payload) {
  const out = {};
  Object.entries(payload).forEach(([k, v]) => {
    if (v === null || v === undefined) return;
    if (Array.isArray(v)) {
      if (v.length === 0) return;
      out[k] = JSON.stringify(v);
    } else if (typeof v === "boolean") {
      out[k] = String(v);
    } else {
      out[k] = v;
    }
  });
  return out;
}

export function buildCrewApiPayload(form) {
  const primaryRole = (form.primary_production_role || "").trim();
  const additionalRoles = form.additional_production_roles || form.positions || [];
  // Combined positions list for backward-compatible API / admin display.
  const positions = [
    ...new Set(
      [primaryRole, ...additionalRoles].filter(Boolean),
    ),
  ];
  const availableAsTalent = form.available_as_talent || "";
  const isCrewPlusTalent = availableAsTalent === "Yes";
  const talentTypes = form.talent_types || [];
  const roleTypes = form.role_types || [];

  return finalizePayload(
    {
      contract_version: CONTRACT_VERSION,
      submission_type: "crew",
      full_name: form.full_name.trim(),
      // Legacy-friendly aliases used by shared email helpers
      name: form.full_name.trim(),
      company_name: trimOrNull(form.company_name),
      email: form.email.trim(),
      phone: form.phone.trim(),
      city: form.city.trim(),
      state: form.state.trim(),
      location: joinLocation(form.city, form.state),
      willing_to_travel: form.willing_to_travel,
      years_of_experience: form.years_of_experience.trim(),
      union_status: form.union_status || null,
      union_status_other: trimOrNull(form.union_status_other),
      own_equipment: form.own_equipment,
      equipment_description:
        form.own_equipment === "Yes" ? form.equipment_description.trim() : null,
      primary_production_role: primaryRole || null,
      additional_production_roles: additionalRoles,
      positions,
      position_other: trimOrNull(form.position_other),
      website: trimOrNull(form.website),
      imdb_link: trimOrNull(form.imdb_link),
      instagram: trimOrNull(form.instagram),
      portfolio: trimOrNull(form.portfolio),
      notes: trimOrNull(form.notes),
      heard_about: form.referral_source || null,
      referral_source: form.referral_source || null,
      referral_source_other: trimOrNull(form.referral_source_other),
      consent: !!form.consent,
      available_as_talent: availableAsTalent || null,
      talent_types: isCrewPlusTalent ? talentTypes : [],
      talent_type: isCrewPlusTalent ? talentTypes[0] || null : null,
      role_types: isCrewPlusTalent ? roleTypes : [],
      playing_age_range: isCrewPlusTalent
        ? trimOrNull(form.playing_age_range)
        : null,
      reel_portfolio_link: isCrewPlusTalent ? trimOrNull(form.reel_link) : null,
      primary_category: "Production Network",
      additional_categories: isCrewPlusTalent ? ["Talent"] : [],
      category_label: isCrewPlusTalent ? "Crew + Talent" : "Production Network",
    },
    form,
  );
}

export function buildProjectApiPayload(form) {
  const genres = form.genres || [];
  const projectType =
    form.project_type === "Other" && form.project_type_other
      ? form.project_type_other.trim()
      : form.project_type;

  return finalizePayload(
    {
      contract_version: CONTRACT_VERSION,
      submission_type: "project",
      // Legacy API fields
      name: form.full_name.trim(),
      email: form.email.trim(),
      phone: form.phone.trim(),
      project_title: form.project_title.trim(),
      project_type_or_area: projectType,
      message: form.message.trim(),
      heard_about: form.referral_source || null,
      consent: !!form.consent,
      // v1 expansions
      full_name: form.full_name.trim(),
      project_type: form.project_type,
      project_type_other: trimOrNull(form.project_type_other),
      genres,
      genre_other: trimOrNull(form.genre_other),
      current_stage: form.current_stage,
      logline: form.logline.trim(),
      referral_source: form.referral_source || null,
      referral_source_other: trimOrNull(form.referral_source_other),
    },
    form,
  );
}

export function buildPartnerApiPayload(form) {
  const partnerTypes = form.partner_types || [];
  const interestType = partnerInterestTypeForSelections(partnerTypes);

  return finalizePayload(
    {
      contract_version: CONTRACT_VERSION,
      submission_type: "partner",
      // Legacy API fields
      name: form.full_name.trim(),
      email: form.email.trim(),
      phone: form.phone.trim(),
      company_brand: trimOrNull(form.company_name),
      location: joinLocation(form.city, form.state),
      interest_type: interestType,
      project_type_or_area: null,
      developing_or_supporting_project: null,
      website_company_link: trimOrNull(form.website),
      message: form.message.trim(),
      heard_about: form.referral_source || null,
      consent: !!form.consent,
      // v1 expansions
      full_name: form.full_name.trim(),
      company_name: trimOrNull(form.company_name),
      city: form.city.trim(),
      state: form.state.trim(),
      industry: trimOrNull(form.industry),
      partner_types: partnerTypes,
      referral_source: form.referral_source || null,
      referral_source_other: trimOrNull(form.referral_source_other),
    },
    form,
  );
}

export function buildContactApiPayload(form) {
  return finalizePayload(
    {
      contract_version: CONTRACT_VERSION,
      submission_type: "contact",
      // Legacy API fields
      name: form.full_name.trim(),
      email: form.email.trim(),
      subject: form.subject.trim(),
      message: form.message.trim(),
      heard_about: form.referral_source || null,
      consent: !!form.consent,
      // v1 expansions
      full_name: form.full_name.trim(),
      phone: trimOrNull(form.phone),
      referral_source: form.referral_source || null,
      referral_source_other: trimOrNull(form.referral_source_other),
    },
    form,
  );
}
